import type { ApiRequestConfig } from "../clients/awsApiClient.ts";
import {
  useApiPostQuery,
  useApiQuery,
  useApiPostMutation,
} from "./useApiQuery.ts";

type ApiKeyChangeResponse = {
  message: string;
};

type NettingAccount = {
  id: string;
  name: string;
  type: string;
  system: string;
  currency: string;
  status: string;
};

type OpenPosition = {
  ticker: string;
  volume: number;
  side: string;
  close_price: number;
  open_price: number;
  open_time: string;
  profit: number;
};

type CurrentAccount = {
  currency: string;
  equity: number;
  equity_usd: number;
  positions: OpenPosition[];
};

type Order = {
  new_position: number;
  order: number;
  position: number;
  price: number;
  score: number;
};

type RebalancePreview = {
  calculated_long: number;
  calculated_short: number;
  capital: number;
  orders: {
    [ticker: string]: Order;
  };
};

export type RebalanceResult = {
  orders_ok: string[];
  positions: {
    volume: number;
    ticker: string;
    status: string;
    side: string;
    open_price: number;
    close_price: number | null;
    open_time: string;
    close_time: string | null;
    profit: number;
  }[];
  problematic_orders: {
    action: string;
    status_code: number;
    ticker: string;
    message: {
      code: string;
      msg: string;
    };
  }[];
};

export function useNettingAccounts(
  config?: ApiRequestConfig,
  queryOptions?: { enabled?: boolean }
) {
  try {
    return useApiQuery<NettingAccount[]>({
      path: `/robomarkets/netting-accounts`,
      config,
      queryKey: ["netting-accounts"],
      ...queryOptions,
    });
  } catch (error) {
    debugger;
    console.error("Error fetching netting accounts:", error);
    throw error;
  }
}

export function useCurrentPositions(
  accountId: string,
  config?: ApiRequestConfig,
  queryOptions?: { enabled?: boolean }
) {
  return useApiQuery<CurrentAccount>({
    path: `/robomarkets/current-positions/${accountId}`,
    config,
    queryKey: ["current-positions", accountId],
    retry: false, // Don't retry on failure to avoid spamming the API
    ...queryOptions,
  });
}

export function useRebalancePreview(
  accountId: string,
  modelId: string,
  config?: ApiRequestConfig,
  queryOptions?: { enabled?: boolean }
) {
  return useApiQuery<RebalancePreview>({
    path: `/robomarkets/rebalance-preview/${accountId}/${modelId}`,
    config,
    queryKey: ["rebalance-preview", accountId, modelId],
    retry: false, // Don't retry on failure to avoid spamming the API
    ...queryOptions,
  });
}

export function usePostRoboMarketsApiKey(config?: ApiRequestConfig) {
  return useApiPostQuery<any>({
    path: `/robomarkets/api-key`,
    config,
    queryKey: ["api-key"],
    retry: false, // Don't retry on failure to avoid spamming the API
  });
}

export function usePostRoboMarketsApiKeyMutation() {
  return useApiPostMutation<ApiKeyChangeResponse, { apiKey: string }>({
    getPath: () => `/robomarkets/api-key`,
    getConfig: (variables) => ({
      body: variables,
    }),
  });
}

export function usePerformRebalance(config?: ApiRequestConfig) {
  return useApiPostQuery<any>({
    path: `/robomarkets/perform-rebalance`,
    config,
    queryKey: ["perform-rebalance"],
    retry: false, // Don't retry on failure to avoid spamming the API
  });
}

export function usePerformRebalanceMutation() {
  return useApiPostMutation<
    RebalanceResult,
    { accountId: string; modelId: string }
  >({
    getPath: () => `/robomarkets/perform-rebalance`,
    getConfig: (variables) => ({
      body: variables,
    }),
  });
}
